import { createSlice } from "@reduxjs/toolkit";
import {
  getUsers,
  getUser,
  addNewUser,
  updateUser,
  deleteUser,
  changePassword,
  changeUserPassword,
  getUserProfile,
  updateUserProfile,
  updatePreferredCurrency,
} from "./thunk";

export const initialState = {
  users: [],
  user: null, // For individual user details
  userProfile: null, // For current user profile
  preferredCurrency: localStorage.getItem("preferredCurrency") || "RON",
  error: null,
  isUserSuccess: false,
  isUserCreated: false,
  isPasswordChanged: false,
  isUserFetched: false,
  isProfileFetched: false,
  isLoading: false,
};

const usersSlice = createSlice({
  name: "UsersSlice",
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear user details
    clearUser: (state) => {
      state.user = null;
      state.isUserFetched = false;
    },
    
    // Reset flags
    resetUserFlags: (state) => {
      state.isUserCreated = false;
      state.isPasswordChanged = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.isUserSuccess = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch users";
        state.isUserSuccess = false;
        state.isLoading = false;
      });

    // Fetch single user
    builder
      .addCase(getUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isUserFetched = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.user = null;
        state.isUserFetched = false;
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch user details";
      });

    // Add new user
    builder
      .addCase(addNewUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isUserCreated = false;
      })
      .addCase(addNewUser.fulfilled, (state, action) => {
        state.users.push(action.payload.data);
        state.isUserCreated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(addNewUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to add user";
        state.isUserCreated = false;
        state.isLoading = false;
      });

    // Update user
    builder
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? { ...user, ...action.payload } : user
        );
        // Also update the individual user if it's the same one
        if (state.user && state.user.id === action.payload.id) {
          state.user = { ...state.user, ...action.payload };
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to update user";
        state.isLoading = false;
      });

    // Delete user
    builder
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        // action.payload is now just the userId (from the updated thunk)
        state.users = state.users.filter(
          (user) => user.id !== action.payload
        );
        // Clear individual user if it was the deleted one
        if (state.user && state.user.id === action.payload) {
          state.user = null;
          state.isUserFetched = false;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete user";
        state.isLoading = false;
      });

    // Change password (current user)
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isPasswordChanged = false;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isPasswordChanged = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isPasswordChanged = false;
        state.isLoading = false;
        state.error = action.payload || "Failed to change password";
      });

    // Change user password (admin action)
    builder
      .addCase(changeUserPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to change user password";
      });

    // Get user profile
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.userProfile = action.payload;
        state.isProfileFetched = true;
        state.isLoading = false;
        state.error = null;
        if (action.payload?.preferred_currency) {
          state.preferredCurrency = action.payload.preferred_currency;
          localStorage.setItem("preferredCurrency", action.payload.preferred_currency);
        }
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.userProfile = null;
        state.isProfileFetched = false;
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch user profile";
      });

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.userProfile = { ...state.userProfile, ...action.payload };
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update user profile";
      });

    builder
      .addCase(updatePreferredCurrency.fulfilled, (state, action) => {
        state.preferredCurrency = action.payload;
        localStorage.setItem("preferredCurrency", action.payload);
      });
  },
});

export const { clearError, clearUser, resetUserFlags } = usersSlice.actions;
export default usersSlice.reducer;